/**
 * Utils simplificado seguindo YAGNI (You Aren't Gonna Need It)
 * Remove funcionalidades desnecessárias e mantém apenas o essencial
 */

import { Truck, FileText } from "lucide-react";
import type { DateISO } from "@PN/inclusao";
import type { TipoDocumento, ValidationResult } from "@PN/inclusao";

// =================================================================
// TIPOS ESSENCIAIS - MOVIDOS PARA z-lib/types/xml.types.ts
// =================================================================

// =================================================================
// UTILS XML - APENAS O ESSENCIAL
// =================================================================

export const xmlUtils = {
  /**
   * Remove caracteres não numéricos da chave XML
   */
  cleanKey(chave: string): string {
    if (!chave || typeof chave !== "string") return "";
    return chave.replace(/[^\d]/g, "");
  },

  /**
   * Valida se uma chave XML é válida
   */
  validateKey(chave: string): ValidationResult {
    if (!chave || typeof chave !== "string") {
      return {
        isValid: false,
        error: "Chave é obrigatória",
      };
    }

    const chaveLimpa = this.cleanKey(chave);

    // ✅ VALIDAÇÃO FLEXÍVEL: Aceita chaves com 44, 50, 52 ou 55 dígitos
    // - 44 dígitos: SPED (NFe padrão), NF3E (66) ou NFCOM (62)
    // - 50 dígitos: NFSE (Nota fiscal de serviços eletrônica)
    // - 52 dígitos: NFe com formato estendido (modelo 96, etc.)
    // - 55 dígitos: CT-e (Conhecimento de Transporte)
    const tamanhosValidos = [44, 50, 52, 55];
    if (!tamanhosValidos.includes(chaveLimpa.length)) {
      return {
        isValid: false,
        error: `Chave deve ter 44 dígitos (SPED/NF3E/NFCOM), 50 dígitos (NFSE), 52 dígitos (NFe estendida) ou 55 dígitos (CT-e). Recebido: ${chaveLimpa.length}`,
      };
    }

    if (!/^\d+$/.test(chaveLimpa)) {
      return {
        isValid: false,
        error: "Chave deve conter apenas números",
      };
    }

    return {
      isValid: true,
    };
  },

  /**
   * Detecta o tipo de documento baseado na chave XML
   */
  detectTypeByKey(chave: string): TipoDocumento | null {
    if (!chave || typeof chave !== "string") return null;

    const chaveLimpa = this.cleanKey(chave);

    // ✅ Verifica primeiro o tamanho da chave para detectar NFSE (50 dígitos)
    if (chaveLimpa.length === 50) {
      return {
        tipo: "NFSE",
        cor: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
        icon: FileText,
      };
    }

    if (chaveLimpa.length < 22) return null;

    try {
      const modelo = chaveLimpa.substring(20, 22);

      if (modelo === "57") {
        return {
          tipo: "CT-e",
          cor: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
          icon: Truck,
        };
      } else if (modelo === "55") {
        return {
          tipo: "SPED",
          cor: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
          icon: FileText,
        };
      } else if (modelo === "66") {
        return {
          tipo: "NF3E",
          cor: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
          icon: FileText,
        };
      } else if (modelo === "62") {
        return {
          tipo: "NFCOM",
          cor: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
          icon: FileText,
        };
      } else {
        // ✅ ACEITAR OUTROS MODELOS: Para documentos com modelo diferente (ex: 96)
        return {
          tipo: "NFe",
          cor: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
          icon: FileText,
        };
      }
    } catch (error) {
      console.warn("Erro ao detectar tipo de documento:", error);
    }

    return null;
  },

  /**
   * Detecta o tipo de documento baseado nos dados do XML
   */
  detectTypeByData(xmlData: any): TipoDocumento | null {
    if (!xmlData) return null;

    try {
      if (Array.isArray(xmlData.itens) && xmlData.itens.length > 0) {
        return {
          tipo: "SPED",
          cor: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
          icon: FileText,
        };
      } else {
        return {
          tipo: "CT-e",
          cor: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
          icon: Truck,
        };
      }
    } catch (error) {
      console.warn("Erro ao detectar tipo de documento dos dados:", error);
    }

    return null;
  },
};

// =================================================================
// UTILS DATA - APENAS O ESSENCIAL
// =================================================================

export const dateUtils = {
  /**
   * Converte data brasileira para formato ISO
   * Suporta múltiplos formatos: dd/MM/yyyy, dd-MM-yyyy, dd.mm.yyyy, etc.
   */
  formatToISO(dateStr: string): DateISO {
    if (!dateStr) return "" as DateISO;

    const cleanDateStr = dateStr.trim().replace(/[\r\n\t]/g, "");

    // Se já estiver no formato ISO, retorna como está
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleanDateStr)) {
      return cleanDateStr as DateISO;
    }

    // Tenta diferentes formatos de data brasileira
    const formats = [
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // dd/mm/yyyy
      /^(\d{1,2})-(\d{1,2})-(\d{4})$/, // dd-mm-yyyy
      /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/, // dd.mm.yyyy
      /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/, // dd/mm/yy
      /^(\d{1,2})-(\d{1,2})-(\d{2})$/, // dd-mm-yy
      /^(\d{1,2})\.(\d{1,2})\.(\d{2})$/, // dd.mm.yy
    ];

    for (const format of formats) {
      const match = cleanDateStr.match(format);
      if (match) {
        const [, day, month, year] = match;
        const paddedDay = day.padStart(2, "0");
        const paddedMonth = month.padStart(2, "0");
        const fullYear = year.length === 2 ? `20${year}` : year;

        return `${fullYear}-${paddedMonth}-${paddedDay}` as DateISO;
      }
    }

    // Tenta extrair números da string
    const numbers = cleanDateStr.replace(/\D/g, "");
    if (numbers.length >= 6) {
      const day = numbers.slice(0, 2);
      const month = numbers.slice(2, 4);
      const year = numbers.slice(4, 8);

      if (year.length === 4) {
        return `${year}-${month}-${day}` as DateISO;
      }
    }

    console.warn("Não foi possível fazer parse da data:", dateStr);
    return "" as DateISO;
  },

  /**
   * Formata data da última compra (YYYYMMDD → DD/MM/YYYY)
   */
  formatUltimaCompra(data: string | undefined): string | undefined {
    if (!data || data.length !== 8) return undefined;

    const ano = data.substring(0, 4);
    const mes = data.substring(4, 6);
    const dia = data.substring(6, 8);

    return `${dia}/${mes}/${ano}`;
  },
};

// =================================================================
// UTILS FORNECEDOR - APENAS O ESSENCIAL
// =================================================================

export const fornecedorUtils = {
  /**
   * Formata telefone completo com DDD
   */
  formatTelefone(ddd?: string, telefone?: string): string {
    if (!ddd?.trim() || !telefone?.trim()) return telefone?.trim() || "";
    return `(${ddd.trim()}) ${telefone.trim()}`;
  },

  /**
   * Formata endereço completo
   */
  formatEndereco(
    endereco?: string,
    municipio?: string,
    estado?: string
  ): string {
    if (!endereco) return "";

    let enderecoCompleto = endereco.trim();

    if (municipio?.trim()) {
      enderecoCompleto += `, ${municipio.trim()}`;
    }

    if (estado?.trim()) {
      enderecoCompleto += ` - ${estado.trim()}`;
    }

    return enderecoCompleto;
  },

  /**
   * Formata CNPJ com máscara
   */
  formatCNPJ(cnpj: string): string {
    if (!cnpj) return "";

    const cleaned = cnpj.replace(/\D/g, "");

    if (cleaned.length !== 14) return cnpj;

    return cleaned.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  },
};

// =================================================================
// UTILS PEDIDO - APENAS O ESSENCIAL
// =================================================================

export const pedidoUtils = {
  /**
   * Gera chave única para pedido
   */
  generateKey(codPedido: string, filPedido: string): string {
    return `${codPedido}-${filPedido}`;
  },

  /**
   * Valida se pedido pode ser selecionado
   */
  canSelectPedido(
    isLoading: boolean,
    error: any,
    listaLength: number,
    tipoNota?: string,
    fornecedor?: { codigo?: string; loja?: string }
  ): boolean {
    return (
      !isLoading &&
      !error &&
      listaLength > 0 &&
      !!tipoNota &&
      !!fornecedor?.codigo &&
      !!fornecedor?.loja
    );
  },
};

// =================================================================
// EXPORT PRINCIPAL SIMPLIFICADO
// =================================================================

export const identificacaoUtils = {
  xml: xmlUtils,
  date: dateUtils,
  fornecedor: fornecedorUtils,
  pedido: pedidoUtils,
} as const;
